import { useState, useEffect, useMemo, useRef } from "react";
import type { Table as TanStackTable, Column } from "@tanstack/react-table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Filter, X } from "lucide-react";
import type { FilterType } from "./types";
import { StringFilter, NumberFilter, EnumFilter } from "./filters";

interface FilterDialogProps<T> {
  table: TanStackTable<T>;
  columnLabels: Record<string, string>;
  columnTypes: Record<string, FilterType>;
  enumValues?: Record<string, Record<string, string>>;
  activeFilterCount: number;
  onClearAll: () => void;
  columnOrder?: string[];
}

export function FilterDialog<T>({
  table,
  columnLabels,
  columnTypes,
  enumValues,
  activeFilterCount,
  onClearAll,
  columnOrder,
}: FilterDialogProps<T>) {
  // Get all filterable columns (exclude select column and columns without filter types)
  // Memoize to prevent infinite loops in useEffect
  const filterableColumns = useMemo(() => {
    const allColumns = table
      .getAllLeafColumns()
      .filter((column) => {
        const columnId = column.id;
        return columnId !== "select" && columnTypes[columnId] !== undefined;
      });
    
    // Sort columns by columnOrder if provided
    if (columnOrder && columnOrder.length > 0) {
      const columnMap = new Map(allColumns.map(col => [col.id, col]));
      const ordered: Column<T, unknown>[] = [];
      const usedIds = new Set<string>();
      
      // Add columns in order
      for (const id of columnOrder) {
        const col = columnMap.get(id);
        if (col) {
          ordered.push(col);
          usedIds.add(id);
        }
      }
      
      // Add any remaining columns that weren't in the order
      for (const col of allColumns) {
        if (!usedIds.has(col.id)) {
          ordered.push(col);
        }
      }
      
      return ordered;
    }
    
    return allColumns;
  }, [table, columnTypes, columnOrder]);

  // State to track which columns have their filter UI displayed
  const [activeColumnIds, setActiveColumnIds] = useState<string[]>([]);
  // State to control Select value reset
  const [selectValue, setSelectValue] = useState<string>("");
  // Track dialog open state to re-initialize when dialog opens
  const [isOpen, setIsOpen] = useState(false);

  // Initialize active columns from columns that already have filter values
  // Re-initialize whenever the dialog opens or filterableColumns changes
  useEffect(() => {
    if (!isOpen) return;
    
    const columnsWithFilters = filterableColumns
      .filter((column) => column.getFilterValue() !== undefined)
      .map((column) => column.id);
    setActiveColumnIds(columnsWithFilters);
  }, [isOpen, filterableColumns]);

  // Get columns that are available to add (not already active)
  const availableColumns = filterableColumns.filter(
    (column) => !activeColumnIds.includes(column.id)
  );

  // Get active column objects
  const activeColumns = filterableColumns.filter((column) =>
    activeColumnIds.includes(column.id)
  );

  const handleAddFilter = (columnId: string) => {
    if (!activeColumnIds.includes(columnId)) {
      setActiveColumnIds([...activeColumnIds, columnId]);
      setSelectValue(""); // Reset select to show placeholder again
    }
  };

  const handleRemoveFilter = (columnId: string) => {
    const column = filterableColumns.find((col) => col.id === columnId);
    if (column) {
      column.setFilterValue(undefined);
    }
    setActiveColumnIds(activeColumnIds.filter((id) => id !== columnId));
  };

  const handleClearAll = () => {
    activeColumns.forEach((column) => {
      column.setFilterValue(undefined);
    });
    setActiveColumnIds([]);
    onClearAll();
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="relative flex-shrink-0">
          <Filter className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Filters</span>
          {activeFilterCount > 0 && (
            <Badge
              variant="secondary"
              className="ml-1 sm:ml-2 h-5 min-w-5 px-1.5 flex items-center justify-center text-xs"
            >
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between pr-12">
            <DialogTitle>Filter Columns</DialogTitle>
            {activeFilterCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearAll}
                className="h-8"
              >
                <X className="h-4 w-4 mr-2" />
                Clear All Filters
              </Button>
            )}
          </div>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {filterableColumns.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No filterable columns available
            </p>
          ) : (
            <>
              {/* Add Filter Dropdown */}
              {availableColumns.length > 0 && (
                <div className="flex items-center gap-2">
                  <Select
                    value={selectValue}
                    onValueChange={(value) => {
                      if (value) {
                        handleAddFilter(value);
                      }
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Add a filter..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableColumns.map((column) => {
                        const columnId = column.id;
                        const label = columnLabels[columnId] || columnId;
                        return (
                          <SelectItem key={columnId} value={columnId}>
                            {label}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Active Filters */}
              {activeColumns.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No filters active. Add a filter using the dropdown above.
                </p>
              ) : (
                activeColumns.map((column) => {
                  const columnId = column.id;
                  const label = columnLabels[columnId] || columnId;
                  const filterType = columnTypes[columnId] || "string";
                  const enumVals = enumValues?.[columnId];

                  return (
                    <div
                      key={columnId}
                      className="flex flex-col gap-2 p-3 border rounded-md"
                    >
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium">{label}</label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveFilter(columnId)}
                          className="h-6 px-2 text-xs"
                        >
                          <X className="h-3 w-3 mr-1" />
                          Remove
                        </Button>
                      </div>
                      <div className="flex items-start">
                        {filterType === "string" && (
                          <StringFilter column={column} />
                        )}
                        {filterType === "number" && (
                          <NumberFilter column={column} />
                        )}
                        {filterType === "enum" && enumVals && (
                          <EnumFilter column={column} enumValues={enumVals} />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

